import type { WorkspacePublicId } from '@hookmyapp/shared';
import { UserFacingError } from '@hookmyapp/observability/errors';
import { launchFacebookLogin } from '@/lib/facebook-login';

type FetchWithAuth = (path: string, options?: RequestInit) => Promise<Response>;

/** Picker row from POST /facebook/pages. The Page token never reaches the browser. */
export interface FacebookPageOption {
  id: string;
  name: string;
  pictureUrl: string | null;
  instagram: { username: string | null } | null;
  connected: 'here' | 'elsewhere' | null;
  disabledReason: 'no_token' | null;
}

export interface FacebookConnectedChannel {
  id: string;
  type: 'facebook' | 'instagram';
}

/** A Page the customer can pick: we hold a token and nobody else holds the Page. */
export function isEligibleFacebookPage(p: FacebookPageOption): boolean {
  return p.disabledReason === null && p.connected !== 'elsewhere';
}

// The dialog relay lands wherever the dashboard is (the Connect Channel modal
// hook listens); this marker tells it the token belongs to a Page connect. It
// lives in sessionStorage so the same-window redirect path (mobile / popup
// blocked) survives the round trip.
const FB_INTENT_KEY = 'fb_login_intent';
// A reconnect is pinned to the Page it was started from; any other Page in
// the picker would connect a new channel and leave this one disconnected.
const FB_RECONNECT_PAGE_KEY = 'fb_login_reconnect_page';

export function readFacebookConnectIntent(): boolean {
  try {
    return sessionStorage.getItem(FB_INTENT_KEY) === 'facebook';
  } catch {
    return false;
  }
}

/** The Page id a reconnect was started for, or null for a plain connect. */
export function readFacebookReconnectPageId(): string | null {
  try {
    return sessionStorage.getItem(FB_RECONNECT_PAGE_KEY);
  } catch {
    return null;
  }
}

export function clearFacebookConnectIntent(): void {
  try {
    sessionStorage.removeItem(FB_INTENT_KEY);
    sessionStorage.removeItem(FB_RECONNECT_PAGE_KEY);
  } catch {
    /* storage unavailable */
  }
}

/**
 * Opens the Facebook dialog (popup, or same window on mobile) for a Page
 * connect, or a reconnect pinned to `reconnectPageId`. Resolves the popup
 * handle: the dialog only relays success, so a closed popup with no token is
 * the cancel signal.
 */
export function startFacebookConnect(
  ws: WorkspacePublicId,
  fetchWithAuth: FetchWithAuth,
  opts: { reconnectPageId?: string } = {},
): Promise<Window | null> {
  try {
    sessionStorage.setItem(FB_INTENT_KEY, 'facebook');
    if (opts.reconnectPageId) sessionStorage.setItem(FB_RECONNECT_PAGE_KEY, opts.reconnectPageId);
    else sessionStorage.removeItem(FB_RECONNECT_PAGE_KEY);
  } catch {
    /* storage unavailable: the popup path still works */
  }
  return launchFacebookLogin(ws, fetchWithAuth, { path: '/facebook/oauth/start', urlKey: 'authorizeUrl' });
}

function json(ws: WorkspacePublicId, body: unknown): RequestInit {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Workspace-Id': ws },
    body: JSON.stringify(body),
  };
}

export async function fetchFacebookPages(
  ws: WorkspacePublicId,
  fetchWithAuth: FetchWithAuth,
  token: { state: string; accessToken: string },
): Promise<FacebookPageOption[]> {
  const res = await fetchWithAuth('/facebook/pages', json(ws, token));
  if (!res.ok) {
    throw new UserFacingError({
      message: `/facebook/pages failed: ${res.status}`,
      code: 'FACEBOOK_PAGES_FAILED',
      details: { status: res.status },
      userMessage: 'Could not load your Facebook Pages. Please try again.',
    });
  }
  const data = (await res.json()) as { pages: FacebookPageOption[] };
  return data.pages;
}

/** Resolves the new facebook channel, or rejects with the server's user-facing message. */
export async function submitFacebookSignup(
  ws: WorkspacePublicId,
  fetchWithAuth: FetchWithAuth,
  body: { state: string; accessToken: string; pageId: string },
): Promise<FacebookConnectedChannel> {
  const res = await fetchWithAuth('/facebook/signup', json(ws, body));
  const data = (await res.json().catch(() => ({}))) as {
    facebookChannel?: FacebookConnectedChannel;
    message?: string;
  };
  if (!res.ok || !data.facebookChannel) {
    // The sanitizing exception filter puts the AppError userMessage in `message`.
    throw new UserFacingError({
      message: `/facebook/signup failed: ${res.status}`,
      code: 'FACEBOOK_SIGNUP_FAILED',
      details: { status: res.status },
      userMessage: data.message ?? 'Could not connect this Page. Please try again.',
    });
  }
  return data.facebookChannel;
}
